import React, { useMemo, useState, useCallback, useLayoutEffect, useRef } from 'react';
import moment from 'moment';
import { cn } from 'App/shadcn/lib/utils';
import showdown from 'showdown';
// @ts-ignore - sanitize-html lacks type definitions
import sanitizeHtml from 'sanitize-html';
import ImageModal from './ImageModal';
import PdfPreviewModal from './PdfPreviewModal';
import T from 'Components/Helpers/translate';
import type { MessageAttachment } from './types';
import { openDrawer } from 'Services/Drawer';
import openAgreement from 'App/helpers/openSubscriptionHelper';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';

const brandInitials = (name?: string): string => {
	const words = (name || '').trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return 'A';
	if (words.length === 1) return words[0].charAt(0).toUpperCase();
	return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

// Always show locale-aware long date + time so each bubble is self-dating.
// Routine threads (e.g. post-meeting) accumulate runs across days under a
// single conversation, where a time-only header can't tell you which day a
// run is from. `LL` renders the month name and keeps the year, localized
// (e.g. "June 1, 2026" / "1 juni 2026").
const formatMessageTimestamp = (date: Date): string => moment(date).format('LL LT');

const converter = new showdown.Converter({
	tables: true,
	simplifiedAutoLink: true,
	strikethrough: true,
	tasklists: true
});

const SANITIZE_CONFIG = {
	allowedTags: [
		'p',
		'div',
		'span',
		'br',
		'hr',
		'h1',
		'h2',
		'h3',
		'h4',
		'h5',
		'h6',
		'strong',
		'em',
		'b',
		'i',
		'u',
		's',
		'del',
		'ul',
		'ol',
		'li',
		'table',
		'thead',
		'tbody',
		'tr',
		'td',
		'th',
		'a',
		'blockquote',
		'pre',
		'code',
		'input',
		'img'
	],
	allowedAttributes: {
		a: ['href', 'target', 'rel'],
		input: ['type', 'checked', 'disabled'],
		code: ['class'],
		td: ['align'],
		th: ['align'],
		img: ['src', 'alt', 'title', 'width', 'height']
	},
	allowedSchemes: ['http', 'https', 'mailto', 'tel', 'upsales'],
	allowedSchemesByTag: {
		img: ['http', 'https']
	}
};

const PROSE_CLASSES = 'pr-prose';

const COPY_ICON =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';

// Wrap fenced code blocks in an "editor window" with a chrome bar (window dots,
// language pill, copy button). Runs on already-sanitized HTML, so the injected
// markup is trusted. The <pre><code> body is preserved verbatim.
const wrapCodeBlocks = (src: string, copyLabel: string): string =>
	src.replace(
		/<pre><code(?:\s+class="([^"]*)")?>([\s\S]*?)<\/code><\/pre>/g,
		(_m: string, cls: string | undefined, code: string) => {
			const lang = (cls || '')
				.split(/\s+/)
				.map(c => c.replace(/^lang(uage)?-/, ''))
				.filter(Boolean)[0];
			const safeLang = (lang || '').replace(/[^a-z0-9+#.-]/gi, '');
			const langTag = safeLang ? `<span class="pr-code-lang">${safeLang}</span>` : '';
			const codeTag = cls ? `<code class="${cls}">` : '<code>';
			return (
				`<figure class="pr-code"><figcaption class="pr-code-bar">` +
				`<span class="pr-code-dots"><i></i><i></i><i></i></span>${langTag}` +
				`<button type="button" class="pr-code-copy">${COPY_ICON}` +
				`<span class="pr-code-copy-text">${copyLabel}</span></button>` +
				`</figcaption><pre>${codeTag}${code}</code></pre></figure>`
			);
		}
	);

const ENTITY_LINK_RE =
	/^upsales:\/\/(client|contact|order|appointment|activity|agreement|todo|phonecall|opportunity|ticket|projectplan|agent|task|routine)\/(\d+)$/;

const getCustomerId = (): string => {
	const match = window.location.hash.match(/^#\/(\d+)\//);
	return match ? match[1] : '1';
};

const openEntityInNewTab = (stateName: string, id: number): boolean => {
	let href = Tools.$state?.href?.(stateName, { id, customerId: getCustomerId() });
	if (!href) return false;
	const hashIndex = href.indexOf('#');
	if (hashIndex !== -1) {
		const hashPart = href.substring(hashIndex);
		href = hashPart.startsWith('#/') ? `/${hashPart}` : `/#${hashPart.substring(1)}`;
	} else if (!href.startsWith('/')) {
		href = `/${href}`;
	}
	window.open(href, '_blank', 'noopener,noreferrer');
	return true;
};

type MentionAgent = { id: number; name: string };

type Props = {
	role: 'user' | 'assistant';
	content: string;
	streaming?: boolean;
	images?: string[];
	attachments?: MessageAttachment[];
	avatarUrl?: string | null;
	avatarColor?: string;
	assistantName?: string;
	regDate?: string;
	agents?: MentionAgent[];
	onAgentClick?: (id: number) => void;
	onTaskClick?: (id: number) => void;
	onRoutineClick?: (id: number) => void;
	/** Optional element rendered inline in the header row, right of name/time. */
	headerAccessory?: React.ReactNode;
};

const attachmentKindFor = (mime: string, name: string): { className: string; label: string } => {
	const m = (mime || '').toLowerCase();
	if (m === 'application/pdf' || /\.pdf$/i.test(name)) return { className: 'pdf', label: 'PDF' };
	if (
		m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
		m === 'application/vnd.ms-excel' ||
		/\.(xlsx|xls)$/i.test(name)
	) {
		return { className: 'xlsx', label: 'XLSX' };
	}
	if (m === 'text/csv' || /\.csv$/i.test(name)) return { className: 'xlsx', label: 'CSV' };
	if (
		m === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
		m === 'application/vnd.ms-powerpoint' ||
		/\.pptx?$/i.test(name)
	) {
		return { className: 'pptx', label: 'PPTX' };
	}
	if (m.startsWith('image/')) return { className: 'image', label: 'IMG' };
	if (m === 'application/json' || /\.json$/i.test(name)) return { className: 'text', label: 'JSON' };
	if (m.startsWith('text/')) return { className: 'text', label: 'TXT' };
	return { className: 'text', label: 'FILE' };
};

const formatBytes = (n?: number): string => {
	if (!n || n <= 0) return '';
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const MessageBlock = ({
	role,
	content,
	streaming = false,
	images,
	attachments,
	avatarUrl,
	avatarColor,
	assistantName,
	regDate,
	agents,
	onAgentClick,
	onTaskClick,
	onRoutineClick,
	headerAccessory
}: Props) => {
	const nonImageAttachments = useMemo(
		() => (attachments ?? []).filter(a => !(a.mimeType || '').toLowerCase().startsWith('image/')),
		[attachments]
	);
	const [previewPdf, setPreviewPdf] = useState<{ url: string; name: string } | null>(null);

	// Downloadable file chips (uploaded files on user messages; agent-generated
	// documents on assistant messages — WEB-7394). Rendered in both role branches.
	const renderAttachmentChips = (justifyClass = '') =>
		nonImageAttachments.length > 0 ? (
			<div className={cn('flex gap-2 mb-2 flex-wrap', justifyClass)}>
				{nonImageAttachments.map((att, i) => {
					const { className, label } = attachmentKindFor(att.mimeType, att.name);
					const size = formatBytes(att.sizeBytes);
					const isPdf = className === 'pdf';
					const url = att.url;
					const Chip = (
						<div className="pr-msg-attachment" title={att.name}>
							<span className={`ws-ctx-thumb ${className}`}>{label}</span>
							<div className="pr-msg-attachment-info">
								<span className="pr-msg-attachment-name">{att.name}</span>
								{size && <span className="pr-msg-attachment-size">{size}</span>}
							</div>
						</div>
					);
					if (!url) {
						return <div key={i}>{Chip}</div>;
					}
					// PDFs preview in-app on a plain click (so iterating doesn't mean
					// download-and-open each time); the href + target keeps modified/
					// middle clicks opening a new tab. Non-PDFs just open/download.
					return (
						<a
							key={i}
							href={url}
							target="_blank"
							rel="noopener noreferrer"
							className="pr-msg-attachment-link"
							onClick={
								isPdf
									? e => {
											if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
											e.preventDefault();
											setPreviewPdf({ url, name: att.name });
									  }
									: undefined
							}
						>
							{Chip}
						</a>
					);
				})}
			</div>
		) : null;
	const [modalImage, setModalImage] = useState<string | null>(null);
	const [avatarError, setAvatarError] = useState(false);

	const fileMatch = content.match(/\s*\[(\d+) file\(s\) attached\]$/);
	const displayContent = fileMatch ? content.replace(fileMatch[0], '').trim() : content;
	const attachmentCount = fileMatch ? parseInt(fileMatch[1], 10) : 0;

	const html = useMemo(() => sanitizeHtml(converter.makeHtml(displayContent), SANITIZE_CONFIG), [displayContent]);

	const styledHtml = useMemo(() => {
		if (!html) return html;
		try {
			let result = html.replace(
				/<a\s+([^>]*?)href="(upsales:\/\/(?:client|contact|order|appointment|activity|agreement|todo|phonecall|opportunity|ticket|projectplan|agent|task|routine)\/\d+)"([^>]*)>/g,
				(_match: string, before: string, href: string, after: string) =>
					`<a ${before}href="${href}"${after} class="pr-entity-chip">`
			);

			const replaceInTextNodes = (
				src: string,
				re: RegExp,
				replacer: (m: string, ...args: string[]) => string
			) => {
				return src.replace(/(<a\b[^>]*>.*?<\/a>)|(<[^>]+>)|([^<]+)/gs, (seg, anchor, tag, text) => {
					if (anchor || tag) return seg;
					return text.replace(re, replacer);
				});
			};

			result = replaceInTextNodes(
				result,
				/\(?\btask\s*#(\d+)\)?/gi,
				(m: string, id: string) =>
					`<a href="upsales://task/${id}" class="pr-entity-chip pr-mention-link">${m}</a>`
			);

			if (agents?.length) {
				const sorted = [...agents].sort((a, b) => b.name.length - a.name.length);
				for (const agent of sorted) {
					const escaped = agent.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
					// eslint-disable-next-line security/detect-non-literal-regexp -- `escaped` is regex-escaped on the line above
					const re = new RegExp(`\\b${escaped}\\b`, 'gi');
					result = replaceInTextNodes(
						result,
						re,
						(m: string) =>
							`<a href="upsales://agent/${agent.id}" class="pr-entity-chip pr-mention-link">${m}</a>`
					);
				}
			}

			// Render a colour swatch inline before any hex code (e.g. brand
			// colours the agent surfaces). Runs on text nodes only, so it never
			// touches hex inside href/style/existing markup. The style value is
			// the exact hex just matched — safe to inline.
			result = replaceInTextNodes(
				result,
				/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g,
				(m: string) => `<span class="pr-color-swatch" style="background-color:${m}"></span>${m}`
			);

			return wrapCodeBlocks(result, T('default.copy'));
		} catch {
			return wrapCodeBlocks(html, T('default.copy'));
		}
	}, [html, agents]);

	const bodyRef = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		if (!streaming) return;
		bodyRef.current?.querySelectorAll('.pr-code pre').forEach(el => {
			el.scrollTop = el.scrollHeight;
		});
	}, [styledHtml, streaming]);

	const handleContentClick = useCallback(
		(e: React.MouseEvent) => {
			const target = e.target as HTMLElement;
			const copyBtn = target.closest('.pr-code-copy');
			if (copyBtn) {
				e.preventDefault();
				const fig = copyBtn.closest('.pr-code');
				const text = fig?.querySelector('pre')?.textContent ?? '';
				const labelEl = copyBtn.querySelector('.pr-code-copy-text');
				navigator.clipboard
					.writeText(text)
					.then(() => {
						if (!fig) return;
						fig.classList.add('is-copied');
						if (labelEl) labelEl.textContent = T('default.copiedToClipboard');
						window.setTimeout(() => {
							fig.classList.remove('is-copied');
							if (labelEl) labelEl.textContent = T('default.copy');
						}, 1600);
					})
					.catch(err => console.error('Failed to copy code block', err));
				return;
			}
			const img = target.closest('img');
			if (img && !target.closest('a')) {
				const src = img.getAttribute('src');
				if (src) {
					e.preventDefault();
					setModalImage(src);
					return;
				}
			}
			const anchor = target.closest('a');
			if (!anchor) return;
			const href = anchor.getAttribute('href');
			if (!href) return;
			const match = href.match(ENTITY_LINK_RE);
			if (!match) return;
			e.preventDefault();
			const entityType = match[1];
			const entityId = Number(match[2]);
			switch (entityType) {
				case 'agent':
					onAgentClick?.(entityId);
					return;
				case 'task':
					onTaskClick?.(entityId);
					return;
				case 'routine':
					onRoutineClick?.(entityId);
					return;
				case 'client':
					openEntityInNewTab('account.dashboard', entityId);
					return;
				case 'contact':
					openEntityInNewTab('contact.dashboard', entityId);
					return;
				case 'order':
				case 'opportunity':
					openEditOrder({ id: entityId });
					return;
				case 'activity':
					Tools.$upModal.open('editActivity', { id: entityId });
					return;
				case 'appointment':
					openEditAppointment({ id: entityId });
					return;
				case 'todo':
					openDrawer('EditTodo', { todo: { id: entityId } });
					return;
				case 'phonecall':
					openDrawer('EditPhonecall', { activity: { id: entityId } });
					return;
				case 'agreement':
					openAgreement({ agreementId: entityId });
					return;
				case 'ticket':
					openDrawer('EditTicket', { ticketId: entityId });
					return;
				case 'projectplan':
					openDrawer('EditProjectPlan', { projectPlanId: entityId });
					return;
				case 'segment':
					openEntityInNewTab('segment', entityId);
					return;
			}
		},
		[onAgentClick, onTaskClick, onRoutineClick]
	);

	if (role === 'user') {
		const hasNonImage = nonImageAttachments.length > 0;
		const hasImages = images && images.length > 0;
		return (
			<>
				<div className="flex justify-end">
					<div className="max-w-[85%]">
						{hasImages && (
							<div className="flex gap-2 mb-2 justify-end flex-wrap">
								{images!.map((src, i) => (
									<img
										key={i}
										src={src}
										alt=""
										className="w-20 h-20 rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
										onClick={() => setModalImage(src)}
									/>
								))}
							</div>
						)}
						{renderAttachmentChips('justify-end')}
						{!hasImages && !hasNonImage && attachmentCount > 0 && (
							<div className="text-body-sm text-muted-foreground mb-1 text-right">
								{T('assistant.filesAttached', { count: attachmentCount })}
							</div>
						)}
						<div
							className="pr-user-bubble"
							onClick={handleContentClick}
							dangerouslySetInnerHTML={{ __html: styledHtml }}
						/>
					</div>
				</div>
				{modalImage && <ImageModal src={modalImage} onClose={() => setModalImage(null)} />}
				{previewPdf && (
					<PdfPreviewModal url={previewPdf.url} name={previewPdf.name} onClose={() => setPreviewPdf(null)} />
				)}
			</>
		);
	}

	return (
		<>
			<div className={cn('pr-asst-msg', !assistantName && 'pr-asst-msg--inline')}>
				<div className="pr-asst-head">
					<div
						className="pr-avatar-sm"
						style={!avatarUrl && avatarColor ? { background: avatarColor } : undefined}
					>
						{avatarUrl && !avatarError ? (
							<img src={avatarUrl} alt="" onError={() => setAvatarError(true)} />
						) : (
							brandInitials(assistantName)
						)}
					</div>
					{assistantName && (
						<div className="flex flex-1 items-center gap-2.5">
							<span className="pr-assistant-name">{assistantName}</span>
							<span className="pr-msg-time">
								{formatMessageTimestamp(regDate ? new Date(regDate) : new Date())}
							</span>
							{headerAccessory && <span className="ml-auto flex items-center">{headerAccessory}</span>}
						</div>
					)}
				</div>
				<div
					ref={bodyRef}
					className={cn(PROSE_CLASSES, streaming && 'pr-caret', 'pr-asst-body')}
					onClick={handleContentClick}
					dangerouslySetInnerHTML={{ __html: styledHtml }}
				/>
				{renderAttachmentChips()}
			</div>
			{modalImage && <ImageModal src={modalImage} onClose={() => setModalImage(null)} />}
			{previewPdf && (
				<PdfPreviewModal url={previewPdf.url} name={previewPdf.name} onClose={() => setPreviewPdf(null)} />
			)}
		</>
	);
};

export default MessageBlock;
